import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { SitesService } from 'src/app/sites/sites.service';
import * as moment from 'moment';
import { DeviceTypeService } from '../../../../device-type.service';
import { LEAD_ACID_ONLY_EXCEPTION_KEYS } from '../../widgets/utilization-metric-box/utilization-metrics.interface';
@Component({
	selector: 'app-site-summary-battery-report',
	templateUrl: './site-summary-battery-report.component.html',
	styleUrls: ['./site-summary-battery-report.component.css']
})
export class SiteSummaryBatteryReportComponent implements OnInit , OnChanges{
	constructor(
		private translation: TranslateService, 
		private siteService: SitesService,
		private commonService: CommonService,
		private deviceTypeService: DeviceTypeService,
	) {}
	
	@Input() sitePerformanceScores: any = {};
	@Input() devices: any = [];
	@Input() siteId!: number;
	@Input() siteInfo!: any;
	@Input() period!: string;

	temperatureUnit = {
		FEH: 0,
		CEL: 1
	};

	siteChargingType: number = 0;
 	usagePerformanceData: any = {};
	highTemperatureLimit: string = '';
	lowSoc: string = '';
	minDailyPlugins: string = '';
	totalDevices: number = 0;
	widgetsConfig: any = {
		batteries_outside_geofence: {
			value: 0,
			color: 'blue',
			icon: 'location_on'
		},
		batteries_with_low_water_level: {
			value: 0,
			color: 'yellow',
			icon: 'warning'
		},
		batteries_with_messed_equalization_charge: {
			value: 0,
			color: 'purple',
			icon: 'warning'
		},
		potential_sulfated_batteries: {
			value: 0,
			color: 'yellow',
			icon: 'warning'
		},
		batteries_with_potential_weak_cells: {
			value: 0,
			color: 'yellow',
			icon: 'warning'
		},
		high_temperature_batteries: {
			value: 0,
		},
		batteries_with_low_soc: {
			value: 0,
		},
		batteries_with_daily_not_plug_ins: {
			value: 0,
		},
	};

	widgetsDataMapper: any = {
		batteries_with_low_water_level: 'lowWaterLevelData',
		batteries_with_messed_equalization_charge: 'messedEQData',
		potential_sulfated_batteries: 'potentialSulfatedData',
		batteries_with_potential_weak_cells: 'potentialWeakCellData',
		high_temperature_batteries: 'highTemperatureBatteriesData',
		batteries_with_low_soc: 'lowSOCBatteriesData',
		batteries_with_daily_not_plug_ins: 'notPluggedInBatteriesData',
	};

	exceptionReportsData: any = {};
	batteries: any[] = [];
	batteryPerformanceMetrics: Array<{ label: string; value: number }> = [];
	chargingMetrics: Array<{ label: string; value: number }> = [];
	siteSettings: any;
	selectedPeriod: string = '';
	numOfWeeks: number = 0;

	ngOnInit(): void {
		this.getSiteUtilizationSettings();
		this.getBatteryExceptionReports();
		this.getBatteryUsagePerformanceReports();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if(changes['period']) {
			this.formatSelectedData();
			this.calculateChargingMetrics();
			const devices =  Object.values(this.devices);
			this.formatPeriod(this.period, devices);
			this.calculateWeeksOfOperation(devices, this.period);
		}
		// siteInfo can arrive after the exception reports response - rebuild so the lead-acid metrics follow the real site
		else if (changes['siteInfo']) {
			this.formatSelectedData();
			this.calculateChargingMetrics();
		}
	}

	formatSelectedData() {
		const labels = [
			'batteries_outside_geofence',
			'batteries_with_low_water_level',
			'potential_sulfated_batteries',
			'batteries_with_potential_weak_cells',
			'batteries_with_messed_equalization_charge',
		].filter(label => this.isMetricVisible(label));

		for (const label of labels) {
			let value: any = 0;
			if (label === 'batteries_outside_geofence') {
				// ST-2689: same rule as the Battery Exception Reports card - flagged batteries out of those with a usable location
				const locatedBatteries = this.batteries.filter(d => d.has_location);
				const totalOutsideGeofenceDevices = locatedBatteries.filter(d => d.is_far_device).length;
				value = locatedBatteries.length ? +((totalOutsideGeofenceDevices / locatedBatteries.length) * 100).toFixed(1) : 0;
			}
			else {
				const dataKey = this.widgetsDataMapper[label];
				const periodData = this.exceptionReportsData?.[dataKey]?.[this.period];

				if (periodData) {
					let includedDevicesCount = 0;
					for (const macAddress in periodData) {
						if (periodData[macAddress].value && periodData[macAddress].showDevice)
							includedDevicesCount++;
					}
					value = this.batteries.length ? +((includedDevicesCount / this.batteries.length) * 100).toFixed(1) : 0;
				}
			}
			this.widgetsConfig[label].value = value;
		}

		this.batteryPerformanceMetrics = labels.map(label => ({
			label: this.translation.instant(`utilization_metrics_print.${label}`),
			value: this.widgetsConfig[label].value,
			color: this.widgetsConfig[label].color,
			icon: this.widgetsConfig[label].icon,
		}));
	}

	calculateChargingMetrics() {
		const labels = [
			'batteries_with_daily_not_plug_ins',
			'batteries_with_low_soc',
			'high_temperature_batteries'
		].filter(label => this.isMetricVisible(label));

		for (const label of labels) {
			let value: any = 0;
			
				const dataKey = this.widgetsDataMapper[label];
				const periodData = this.exceptionReportsData?.[dataKey]?.[this.period];

				if (periodData) {
					let includedDevicesCount = 0;
					for (const macAddress in periodData) {
						if (periodData[macAddress].value && periodData[macAddress].showDevice)
							includedDevicesCount++;
					}
					value = this.batteries.length ? +((includedDevicesCount / this.batteries.length) * 100).toFixed(1) : 0;
				}

			this.widgetsConfig[label].value = value;
		}

		this.chargingMetrics = labels.map(label => ({
			label: this.translation.instant(`utilization_metrics_print.${label}`),
			value: this.widgetsConfig[label].value,
			siteValue: this.getSiteValue(label),
		}));
	}

	isMetricVisible(label: string): boolean {
		if (!LEAD_ACID_ONLY_EXCEPTION_KEYS.has(label))
			return true;

		// Hidden until the real site is known, so lead-acid metrics never flash on a lithium-only site
		return this.siteInfo?.id == this.siteId && !this.deviceTypeService.isLithiumOnlyBattlinkSite(this.siteInfo);
	}

	getSiteUtilizationSettings() {
		this.siteService.getSiteUtilizationSettings(this.siteId).subscribe((res: any) => {
			this.siteSettings = res[0].batteries_utilization_settings;
			this.highTemperatureLimit = this.siteSettings.temperature_unit != this.temperatureUnit.CEL ? `${this.siteSettings.high_temperature_limit}°F` : `${this.commonService.fahToCel(this.siteSettings.high_temperature_limit)}°C`;
			this.lowSoc = this.siteSettings.min_daily_battery_soc + '%';
			this.minDailyPlugins = this.siteSettings.min_daily_plug_ins;
			this.siteChargingType = this.siteSettings.charging_type;
		});
	}

	getBatteryUsagePerformanceReports() {
		this.siteService.getBatteryUsagePerformanceReports(this.siteId).subscribe((response: any) => {
			this.totalDevices = response.devices.length;
			this.usagePerformanceData = response;
		});
	}

	getBatteryExceptionReports() {
		this.siteService.getBatteryExceptionReports(this.siteId).subscribe((response: any) => {
			this.exceptionReportsData = response;
			this.batteries = response.devices || [];
			this.formatSelectedData();
			this.calculateChargingMetrics();
		});
	}

	getSiteValue(label: string): string {
		switch (label) {
			case 'high_temperature_batteries':
				return this.highTemperatureLimit;

			case 'batteries_with_daily_not_plug_ins':
				return this.minDailyPlugins;

			case 'batteries_with_low_soc':
				return this.lowSoc;

			default:
				return '';
		}
	}

	calculateWeeksOfOperation(devices: any[], period: string) {
		if (!devices?.length) 
			return 0;

		const oldestTimestamp = Math.min(...devices.filter(d => d.installation_date).map(d => Number(d.installation_date)));

		if (!oldestTimestamp) 
			return 0;

		const { from, to } = this.commonService.getDateRange(period, oldestTimestamp);

		const start = moment(from);
		const end = moment(to);

		// Move start forward to the next Monday if it's not already Monday
		const firstMonday = start.isoWeekday() === 1 ? start.clone().startOf('day') : start.clone().startOf('isoWeek').add(1, 'week');

		// Move end back to the previous Sunday if it's not already Sunday
		const lastSunday = end.isoWeekday() === 7 ? end.clone().endOf('day') : end.clone().startOf('isoWeek').subtract(1, 'day').endOf('day');

		if (firstMonday.isAfter(lastSunday)) {
			this.numOfWeeks = 0;
			return;
		}

		this.numOfWeeks = Math.floor((lastSunday.clone().startOf('day').diff(firstMonday, 'days') + 1) / 7);
	}


	getStartOfWeekMonday(date: any){
		return moment(date).startOf('isoWeek').startOf('day').toDate();
	};

	formatPeriod(period: string, devices: any[]) {
		const oldestTimestamp = Math.min(...devices.filter((d: any) => d.installation_date).map((d: any) => Number(d.installation_date)));
		const { from, to } = this.commonService.getDateRange(period, oldestTimestamp);
		this.selectedPeriod = `${moment(from).format('M/D/YYYY')} - ${moment(to).format('M/D/YYYY')}`;
	}
}
